import React, { useEffect, useRef, useState } from 'react';
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

const RelationshipGraphView = ({ domains, subdomains, ipAddresses }) => {
  const canvasRef = useRef();
  const [selectedNode, setSelectedNode] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [nodes, setNodes] = useState([]);
  const [links, setLinks] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedNode, setDraggedNode] = useState(null);

  // Force-directed graph simulation
  class Node {
    constructor(id, label, type, sourceData) {
      this.id = id;
      this.label = label;
      this.type = type;
      this.sourceData = sourceData;
      this.x = Math.random() * 800 - 400;
      this.y = Math.random() * 600 - 300;
      this.vx = 0;
      this.vy = 0;
      this.radius = type === 'domain' ? 20 : type === 'port' ? 12 : 15;
      this.color = this.getColor();
    }

    getColor() {
      switch (this.type) {
        case 'domain':
          return '#7c3aed';
        case 'subdomain':
          return '#3b82f6';
        case 'ip':
          return '#10b981';
        case 'port':
          return '#f59e0b';
        default:
          return '#6b7280';
      }
    }

    applyForce(fx, fy, dt = 0.016) {
      const mass = 1;
      this.vx += (fx / mass) * dt;
      this.vy += (fy / mass) * dt;

      // Damping
      this.vx *= 0.98;
      this.vy *= 0.98;

      this.x += this.vx * dt;
      this.y += this.vy * dt;
    }
  }

  class Link {
    constructor(source, target, type) {
      this.source = source;
      this.target = target;
      this.type = type;
    }
  }

  // Build graph data
  useEffect(() => {
    const newNodes = [];
    const newLinks = [];
    const addedNodeIds = new Set();

    // Add domains
    domains.forEach(domain => {
      if (!addedNodeIds.has(domain.domain)) {
        newNodes.push(
          new Node(domain.domain, domain.domain, 'domain', domain)
        );
        addedNodeIds.add(domain.domain);
      }
    });

    // Add subdomains
    subdomains.forEach(subdomain => {
      const subdomainId = subdomain.full_subdomain;
      if (!addedNodeIds.has(subdomainId)) {
        newNodes.push(
          new Node(subdomainId, subdomainId, 'subdomain', subdomain)
        );
        addedNodeIds.add(subdomainId);
      }

      // Link to parent domain
      const parentDomain = subdomain.parent_domain;
      if (parentDomain && domains.find(d => d.domain === parentDomain)) {
        newLinks.push(new Link(parentDomain, subdomainId, 'subdomain'));
      }
    });

    // Add IPs
    ipAddresses.forEach(ip => {
      if (!addedNodeIds.has(ip.ip_address)) {
        newNodes.push(
          new Node(ip.ip_address, ip.ip_address, 'ip', ip)
        );
        addedNodeIds.add(ip.ip_address);
      }

      // Add common ports
      ['80', '443', '22', '25', '3306', '8080', '5432'].forEach(port => {
        const portId = `${ip.ip_address}:${port}`;
        if (!addedNodeIds.has(portId)) {
          newNodes.push(
            new Node(portId, port, 'port', { ip: ip.ip_address, port })
          );
          addedNodeIds.add(portId);
          newLinks.push(new Link(ip.ip_address, portId, 'open-port'));
        }
      });
    });

    setNodes(newNodes);
    setLinks(newLinks);
  }, [domains, subdomains, ipAddresses]);

  // Static layout - no animation
  useEffect(() => {
    if (nodes.length === 0) return;

    // Calculate static positions using a hierarchical circular layout
    const radius = 300;

    // Group nodes by type
    const domainNodes = nodes.filter(n => n.type === 'domain');
    const subdomainNodes = nodes.filter(n => n.type === 'subdomain');
    const ipNodes = nodes.filter(n => n.type === 'ip');
    const portNodes = nodes.filter(n => n.type === 'port');

    // Position domains in center
    domainNodes.forEach((node, i) => {
      const angle = (i / domainNodes.length) * Math.PI * 2;
      node.x = Math.cos(angle) * 100;
      node.y = Math.sin(angle) * 100;
      node.vx = 0;
      node.vy = 0;
    });

    // Position subdomains around their parent domains
    subdomainNodes.forEach((node, i) => {
      const parentDomain = domainNodes.find(d => d.id === node.sourceData.parent_domain);
      if (parentDomain) {
        const angle = (i / subdomainNodes.length) * Math.PI * 2;
        node.x = parentDomain.x + Math.cos(angle) * 150;
        node.y = parentDomain.y + Math.sin(angle) * 150;
      } else {
        const angle = (i / subdomainNodes.length) * Math.PI * 2;
        node.x = Math.cos(angle) * 200;
        node.y = Math.sin(angle) * 200;
      }
      node.vx = 0;
      node.vy = 0;
    });

    // Position IPs in outer ring
    ipNodes.forEach((node, i) => {
      const angle = (i / ipNodes.length) * Math.PI * 2;
      node.x = Math.cos(angle) * radius;
      node.y = Math.sin(angle) * radius;
      node.vx = 0;
      node.vy = 0;
    });

    // Position ports near their IPs
    portNodes.forEach((node, i) => {
      const parentIP = ipNodes.find(ip => node.sourceData.ip === ip.id);
      if (parentIP) {
        const portsForThisIP = portNodes.filter(p => p.sourceData.ip === parentIP.id);
        const portIndex = portsForThisIP.indexOf(node);
        const angle = (portIndex / portsForThisIP.length) * Math.PI * 2;
        node.x = parentIP.x + Math.cos(angle) * 50;
        node.y = parentIP.y + Math.sin(angle) * 50;
      }
      node.vx = 0;
      node.vy = 0;
    });

    // Draw once after layout is complete
    draw();
  }, [nodes, links]);

  // Redraw when view changes (zoom, pan, selection)
  useEffect(() => {
    draw();
  }, [zoom, pan, selectedNode]);

  // Drawing function
  const draw = () => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    canvas.width = rect.width;
    canvas.height = rect.height;

    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas with gradient background
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, 'rgba(15, 23, 42, 0.6)');
    gradient.addColorStop(1, 'rgba(15, 23, 42, 0.4)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Transform for pan and zoom
    ctx.save();
    ctx.translate(width / 2, height / 2);
    ctx.translate(pan.x, pan.y);
    ctx.scale(zoom, zoom);

    // Draw links
    ctx.strokeStyle = '#9ca3af';
    ctx.lineWidth = 1.5 / zoom;
    links.forEach(link => {
      const source = nodes.find(n => n.id === link.source);
      const target = nodes.find(n => n.id === link.target);

      if (source && target) {
        // Draw line
        ctx.beginPath();
        ctx.moveTo(source.x, source.y);
        ctx.lineTo(target.x, target.y);
        ctx.stroke();

        // Draw arrow
        const angle = Math.atan2(target.y - source.y, target.x - source.x);
        const arrowSize = 8 / zoom;
        const startX = target.x - Math.cos(angle) * target.radius;
        const startY = target.y - Math.sin(angle) * target.radius;

        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(
          startX - Math.cos(angle - Math.PI / 6) * arrowSize,
          startY - Math.sin(angle - Math.PI / 6) * arrowSize
        );
        ctx.lineTo(
          startX - Math.cos(angle + Math.PI / 6) * arrowSize,
          startY - Math.sin(angle + Math.PI / 6) * arrowSize
        );
        ctx.closePath();
        ctx.fillStyle = '#9ca3af';
        ctx.fill();
      }
    });

    // Draw nodes
    nodes.forEach(node => {
      ctx.beginPath();
      ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
      ctx.fillStyle = node.color;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2 / zoom;
      ctx.stroke();

      // Highlight selected
      if (selectedNode?.id === node.id) {
        ctx.strokeStyle = '#22d3ee';
        ctx.lineWidth = 3 / zoom;
        ctx.stroke();
      }
    });

    // Draw labels
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `${10 / zoom}px sans-serif`;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 3;

    nodes.forEach(node => {
      const label = node.type === 'domain' ? node.label.split('.')[0] : 
                    node.type === 'port' ? node.label : 
                    node.label.length > 15 ? node.label.substring(0, 12) + '...' : node.label;
      ctx.fillText(label, node.x, node.y);
    });

    ctx.restore();
  };

  // Mouse handlers
  const handleMouseDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2 - pan.x) / zoom;
    const y = (e.clientY - rect.top - rect.height / 2 - pan.y) / zoom;

    const clicked = nodes.find(node => {
      const dx = node.x - x;
      const dy = node.y - y;
      return Math.sqrt(dx * dx + dy * dy) < node.radius * 1.5;
    });

    if (clicked) {
      setSelectedNode(clicked);
      setDraggedNode(clicked);
      clicked.vx = 0;
      clicked.vy = 0;
    }

    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !draggedNode) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2 - pan.x) / zoom;
    const y = (e.clientY - rect.top - rect.height / 2 - pan.y) / zoom;

    // Calculate offset from previous position
    const dx = x - draggedNode.x;
    const dy = y - draggedNode.y;

    // Move the dragged node
    draggedNode.x = x;
    draggedNode.y = y;

    // Find and move all connected child nodes
    // For IPs, move their ports
    if (draggedNode.type === 'ip') {
      const connectedPorts = nodes.filter(node => {
        return node.type === 'port' && 
               links.some(link => link.source === draggedNode.id && link.target === node.id);
      });
      connectedPorts.forEach(port => {
        port.x += dx;
        port.y += dy;
      });
    }
    
    // For domains, move their subdomains
    if (draggedNode.type === 'domain') {
      const connectedSubdomains = nodes.filter(node => {
        return node.type === 'subdomain' && 
               links.some(link => link.source === draggedNode.id && link.target === node.id);
      });
      connectedSubdomains.forEach(subdomain => {
        subdomain.x += dx;
        subdomain.y += dy;
      });
    }
    
    // Redraw immediately when dragging
    draw();
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDraggedNode(null);
  };

  const handleZoomIn = () => {
    setZoom(z => Math.min(z * 1.2, 5));
  };

  const handleZoomOut = () => {
    setZoom(z => Math.max(z / 1.2, 0.2));
  };

  const handleReset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setSelectedNode(null);
  };

  const handleCanvasWheel = (e) => {
    e.preventDefault();
    if (e.deltaY > 0) {
      handleZoomOut();
    } else {
      handleZoomIn();
    }
  };

  return (
    <div className="h-full flex bg-slate-900/20">
      {/* Graph Container */}
      <div className="flex-1 relative overflow-hidden">
        {nodes.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            <p>No assets available to visualize. Add domains, subdomains, or IP addresses to see the relationship graph.</p>
          </div>
        ) : (
          <>
            <canvas
              ref={canvasRef}
              className="w-full h-full cursor-grab active:cursor-grabbing"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onWheel={handleCanvasWheel}
            />

            {/* Controls */}
            <div className="absolute top-4 right-4 flex gap-2 bg-slate-800/90 backdrop-blur rounded-lg shadow-md p-2 border border-slate-700/50">
              <button
                onClick={handleZoomIn}
                className="p-2 hover:bg-slate-700 rounded transition-colors text-slate-300"
                title="Zoom In"
              >
                <ZoomIn size={18} />
              </button>
              <button
                onClick={handleZoomOut}
                className="p-2 hover:bg-slate-700 rounded transition-colors text-slate-300"
                title="Zoom Out"
              >
                <ZoomOut size={18} />
              </button>
              <button
                onClick={handleReset}
                className="p-2 hover:bg-slate-700 rounded transition-colors text-slate-300"
                title="Reset View"
              >
                <RotateCcw size={18} />
              </button>
            </div>

            {/* Legend */}
            <div className="absolute bottom-4 left-4 bg-slate-800/90 backdrop-blur rounded-lg shadow-md p-4 border border-slate-700/50 max-w-xs">
              <h3 className="font-semibold text-slate-200 mb-3 text-sm">Legend</h3>
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full" style={{ backgroundColor: '#7c3aed' }}></div>
                  <span className="text-xs text-slate-300">Domain</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#3b82f6' }}></div>
                  <span className="text-xs text-slate-300">Subdomain</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#10b981' }}></div>
                  <span className="text-xs text-slate-300">IP Address</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#f59e0b' }}></div>
                  <span className="text-xs text-slate-300">Port</span>
                </div>
              </div>
              <div className="text-xs text-slate-400 border-t border-slate-700 pt-2 space-y-1">
                <p>â€¢ Drag nodes to reposition</p>
                <p>â€¢ Scroll wheel to zoom</p>
                <p>â€¢ Click to select</p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Details Panel */}
      {selectedNode && (
        <div className="w-80 border-l border-slate-700/50 bg-slate-900/50 overflow-y-auto shadow-lg">
          <div className="p-4 relative">
            <button
              onClick={() => setSelectedNode(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-300 text-xl"
            >
              âœ•
            </button>

            <div className="flex items-center gap-3 mb-4 pr-8">
              <div
                className="w-8 h-8 rounded-full flex-shrink-0"
                style={{ backgroundColor: selectedNode.color }}
              ></div>
              <div>
                <h3 className="font-semibold text-slate-100 break-words">{selectedNode.label}</h3>
                <p className="text-xs text-slate-400 capitalize">{selectedNode.type}</p>
              </div>
            </div>

            <div className="bg-slate-800/50 rounded-lg p-3 mb-4">
              <p className="text-xs font-mono text-cyan-400 break-all">{selectedNode.id}</p>
            </div>

            <div className="space-y-3">
              {selectedNode.type === 'domain' && (
                <div className="text-sm text-slate-300 space-y-2">
                  <div className="flex justify-between border-b border-slate-700/30 pb-2">
                    <span className="text-slate-400">Type:</span>
                    <span>Primary Domain</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-700/30 pb-2">
                    <span className="text-slate-400">Status:</span>
                    <span className="text-green-400">Active</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Subdomains:</span>
                    <span>{subdomains.filter(s => s.parent_domain === selectedNode.id).length}</span>
                  </div>
                </div>
              )}
              {selectedNode.type === 'subdomain' && (
                <div className="text-sm text-slate-300 space-y-2">
                  <div className="flex justify-between border-b border-slate-700/30 pb-2">
                    <span className="text-slate-400">Type:</span>
                    <span>Subdomain</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-700/30 pb-2">
                    <span className="text-slate-400">Status:</span>
                    <span className="text-green-400">Active</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Parent:</span>
                    <span className="text-xs">{selectedNode.sourceData.parent_domain}</span>
                  </div>
                </div>
              )}
              {selectedNode.type === 'ip' && (
                <div className="text-sm text-slate-300 space-y-2">
                  <div className="flex justify-between border-b border-slate-700/30 pb-2">
                    <span className="text-slate-400">Type:</span>
                    <span>IP Address</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-700/30 pb-2">
                    <span className="text-slate-400">Status:</span>
                    <span className="text-green-400">Active</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Open Ports:</span>
                    <span>{links.filter(l => l.source === selectedNode.id && l.type === 'open-port').length}</span>
                  </div>
                </div>
              )}
              {selectedNode.type === 'port' && (
                <div className="text-sm text-slate-300 space-y-2">
                  <div className="flex justify-between border-b border-slate-700/30 pb-2">
                    <span className="text-slate-400">Type:</span>
                    <span>Service Port</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-700/30 pb-2">
                    <span className="text-slate-400">Status:</span>
                    <span className="text-orange-400">Open</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Port Number:</span>
                    <span className="font-mono">{selectedNode.label}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RelationshipGraphView;